'use client'

import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Dialog } from '@base-ui/react/dialog'
import {
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuRoute,
  LuX,
} from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { cn } from '@/core/utils/common'

import type { CSSProperties, RefObject } from 'react'

export type GuidedTourStep = {
  target: string | (() => HTMLElement | null)
  title: string
  description: string
}

type Rect = { left: number; top: number; width: number; height: number }
type Side = 'above' | 'below' | 'left' | 'right' | 'none'
type Layout = {
  rect: Rect | null
  left: number
  top: number
  width: number
  height: number
  maxHeight: number
  arrow: number | null
  side: Side
}

const TIP_HALF = 4
const TIP_ROTATION: Record<Side, number> = {
  below: 0,
  above: 180,
  right: -90,
  left: 90,
  none: 0,
}

function tipStyle({
  left,
  top,
  width,
  height,
  arrow,
  side,
}: Layout): CSSProperties {
  const along = arrow ?? 0
  const center =
    side === 'below'
      ? { left: left + along, top: top - TIP_HALF }
      : side === 'above'
        ? { left: left + along, top: top + height + TIP_HALF }
        : side === 'right'
          ? { left: left - TIP_HALF, top: top + along }
          : { left: left + width + TIP_HALF, top: top + along }
  return {
    ...center,
    translate: '-50% -50%',
    rotate: `${TIP_ROTATION[side]}deg`,
  }
}

/** 유리 판의 꼭지. 유리 판 안에 넣으면 블러가 꺼져서 판 바깥의 형제로 둔다. 기본은 위를 가리키는 모양 */
export function GlassTip({
  className,
  style,
}: {
  className?: string
  style?: CSSProperties
}) {
  return (
    <span
      aria-hidden="true"
      style={style}
      className={cn(
        'glass-overlay h-2 w-4 shadow-none! [clip-path:polygon(50%_0,100%_100%,0_100%)]',
        className
      )}
    >
      <svg viewBox="0 0 16 8" className="absolute inset-0 size-full">
        <path d="M0 8 8 0 16 8" fill="none" stroke="rgb(255 255 255 / 0.12)" />
      </svg>
    </span>
  )
}

const visible = (element: HTMLElement) =>
  element.getClientRects().length > 0 &&
  element.getBoundingClientRect().width > 0
export function guidedTourTarget(target: GuidedTourStep['target']) {
  if (typeof target === 'function') {
    const element = target()
    return element && visible(element) ? element : null
  }
  return (
    Array.from(document.querySelectorAll<HTMLElement>(target)).find(visible) ??
    null
  )
}

/** 대상 위아래의 여백에 설명을 배치한다. 배경 차단·포커스·Escape는 기존 Base UI Dialog가 관리한다. */
export default function GuidedTour({
  steps,
  onClose,
  returnFocus,
  label = '가이드',
}: {
  steps: GuidedTourStep[]
  onClose: () => void
  returnFocus: RefObject<HTMLButtonElement | null>
  label?: string
}) {
  const [index, setIndex] = useState(0)
  const [layout, setLayout] = useState<Layout | null>(null)
  const [popup, setPopup] = useState<HTMLDivElement | null>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const maskId = useId()
  const step = steps[index]
  const last = index === steps.length - 1
  const next = () => (last ? onClose() : setIndex((value) => value + 1))

  useLayoutEffect(() => {
    const element = guidedTourTarget(step.target)
    let frame = 0
    const viewport = window.visualViewport
    const measure = () => {
      const vw = viewport?.width ?? window.innerWidth
      const vh = viewport?.height ?? window.innerHeight
      const offsetX = viewport?.offsetLeft ?? 0
      const offsetY = viewport?.offsetTop ?? 0
      const minX = offsetX + 12
      const minY = offsetY + 12
      const width = Math.min(344, vw - 24)
      const height = Math.min(popup?.offsetHeight || 228, vh - 24)
      const raw = element?.isConnected ? element.getBoundingClientRect() : null
      const x = Math.max(offsetX + 4, (raw?.left ?? 0) - 6)
      const y = Math.max(offsetY + 4, (raw?.top ?? 0) - 6)
      const right = Math.min(offsetX + vw - 4, (raw?.right ?? 0) + 6)
      const bottom = Math.min(offsetY + vh - 4, (raw?.bottom ?? 0) + 6)
      const rect =
        raw && right > x && bottom > y
          ? { left: x, top: y, width: right - x, height: bottom - y }
          : null
      const center = rect ? rect.left + rect.width / 2 : offsetX + vw / 2
      let left = Math.max(
        minX,
        Math.min(center - width / 2, offsetX + vw - width - 12)
      )
      const below = rect
        ? rect.top + rect.height + 14
        : offsetY + (vh - height) / 2
      let side: Side = 'none'
      let top = below
      if (rect) {
        if (below + height <= offsetY + vh - 12) side = 'below'
        else if (rect.top - height - 14 >= minY) {
          side = 'above'
          top = rect.top - height - 14
        } else if (rect.left + rect.width + width + 26 <= offsetX + vw) {
          side = 'right'
          left = rect.left + rect.width + 14
        } else if (rect.left - width - 14 >= minX) {
          side = 'left'
          left = rect.left - width - 14
        }
        if (side === 'left' || side === 'right')
          top = rect.top + rect.height / 2 - height / 2
      }
      top = Math.max(minY, Math.min(top, offsetY + vh - height - 12))
      let arrow: number | null = null
      if (rect && (side === 'left' || side === 'right'))
        arrow = Math.max(
          20,
          Math.min(rect.top + rect.height / 2 - top, height - 20)
        )
      if (side === 'above' || side === 'below')
        arrow = Math.max(20, Math.min(center - left, width - 20))
      setLayout({
        rect,
        left,
        top,
        width,
        height,
        maxHeight: vh - 24,
        arrow,
        side,
      })
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    if (element) {
      const bounds = element.getBoundingClientRect()
      if (bounds.top < 96 || bounds.bottom > window.innerHeight - 24) {
        element.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: 'instant',
        })
      }
    }
    measure()
    heading.current?.focus({ preventScroll: true })
    const observer = new ResizeObserver(schedule)
    if (element) observer.observe(element)
    if (popup) observer.observe(popup)
    document.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    viewport?.addEventListener('resize', schedule)
    viewport?.addEventListener('scroll', schedule)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      document.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      viewport?.removeEventListener('resize', schedule)
      viewport?.removeEventListener('scroll', schedule)
    }
  }, [step, popup])

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      disablePointerDismissal
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[5000300]">
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 size-full"
          >
            <defs>
              <mask id={maskId}>
                <rect width="100%" height="100%" fill="white" />
                {layout?.rect && (
                  <rect
                    width={layout.rect.width}
                    height={layout.rect.height}
                    x={layout.rect.left}
                    y={layout.rect.top}
                    rx="14"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="black"
              fillOpacity="0.68"
              mask={`url(#${maskId})`}
            />
            {layout?.rect && (
              <rect
                x={layout.rect.left}
                y={layout.rect.top}
                width={layout.rect.width}
                height={layout.rect.height}
                rx="14"
                fill="none"
                className="stroke-point"
                strokeWidth="2"
              />
            )}
          </svg>
        </Dialog.Backdrop>
        <Dialog.Popup
          ref={setPopup}
          initialFocus={heading}
          finalFocus={returnFocus}
          style={
            layout
              ? {
                  left: layout.left,
                  top: layout.top,
                  width: layout.width,
                  maxHeight: layout.maxHeight,
                }
              : {
                  left: 12,
                  top: 12,
                  width: 'calc(100% - 24px)',
                  visibility: 'hidden',
                }
          }
          className="glass-overlay fixed z-[5000301] max-h-[calc(100dvh-24px)] rounded-overlay text-foreground outline-none"
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault()
              next()
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault()
              setIndex((value) => Math.max(0, value - 1))
            }
          }}
        >
          <div
            style={{ maxHeight: layout?.maxHeight }}
            className="relative max-h-[calc(100dvh-24px)] overflow-y-auto p-5"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-c12 font-semibold text-point over10:text-c13">
                <LuRoute aria-hidden="true" className="size-4" />
                {label}
              </span>
              <Dialog.Close
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="가이드 닫기"
                  />
                }
              >
                <LuX aria-hidden="true" className="size-4" />
              </Dialog.Close>
            </div>
            <div
              key={index}
              aria-live="polite"
              aria-atomic="true"
              className="motion-safe:animate-[fade-in_160ms_ease-out]"
            >
              <p className="mb-1 text-c12 text-description tabular-nums over10:text-c13">
                {index + 1} / {steps.length}
              </p>
              <Dialog.Title
                ref={heading}
                tabIndex={-1}
                className="text-c18 leading-7 font-bold outline-none over10:text-c20"
              >
                {step.title}
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-c14 leading-relaxed text-gray over10:text-c15">
                {step.description}
              </Dialog.Description>
            </div>
            <div className="mt-5 flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>
                건너뛰기
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="icon"
                  aria-label="이전 설명"
                  disabled={index === 0}
                  onClick={() => setIndex((value) => value - 1)}
                >
                  <LuArrowLeft aria-hidden="true" className="size-4" />
                </Button>
                <Button size="md" onClick={next}>
                  {last ? '완료' : '다음'}
                  {last ? (
                    <LuCheck aria-hidden="true" className="size-4" />
                  ) : (
                    <LuArrowRight aria-hidden="true" className="size-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Dialog.Popup>
        {layout && layout.arrow !== null && (
          <GlassTip style={tipStyle(layout)} className="fixed z-[5000301]" />
        )}
      </Dialog.Portal>
    </Dialog.Root>
  )
}
